import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Header,
  Segment,
  Button,
  Loader,
  Message,
  Icon,
  Label,
  List,
  Grid,
  Divider,
  Confirm,
} from "semantic-ui-react";
import { getAuthToken, getClientAud } from "../selectors";
import WiderrufFootnoteLink from "./WiderrufFootnoteLink";

function formatPlanMonths(months) {
  const n = Number(months) || 1;
  if (n === 1) return "1 Monat";
  if (n === 6) return "6 Monate";
  if (n === 12) return "12 Monate";
  return `${n} Monate`;
}

function formatMoneyEUR(priceCents, currency) {
  const cur = (currency || "eur").toUpperCase();
  const amount = (Number(priceCents) || 0) / 100;
  return `${amount.toFixed(2)} ${cur}`;
}

function formatDateDE(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (_) {
    return "—";
  }
}

/** @param {Record<string, unknown>} details */
function getPaymentProfile(details) {
  const provider = (details.billing_provider || "stripe").toString();
  if (provider === "paypal") {
    return {
      key: "paypal",
      title: "PayPal",
      short: "PayPal",
      description:
        "Dein Abonnement läuft über PayPal. Zahlungsmittel, Betrag und Kündigung änderst du in deinem PayPal-Konto unter automatischen Zahlungen.",
      icon: "paypal",
      accent: "#0070ba",
    };
  }
  const pm = details.stripe_payment_method_type;
  if (pm === "sepa_debit") {
    return {
      key: "sepa",
      title: "SEPA-Lastschrift",
      short: "Lastschrift (Stripe)",
      description:
        "Abbuchungen laufen per SEPA-Lastschrift über Stripe. Rechnungen, Bankverbindung und Kündigung erledigst du im Stripe-Kundenportal.",
      icon: "university",
      accent: "#00a3a0",
    };
  }
  if (pm === "card" || pm === "link") {
    return {
      key: "card",
      title: pm === "link" ? "Link oder Karte" : "Karte / Apple Pay",
      short: pm === "link" ? "Stripe Link" : "Karte (Stripe)",
      description:
        "Zahlung über Stripe (Karte, Apple Pay oder Link). Abo, Rechnungen und Zahlungsmittel im Stripe-Kundenportal verwalten.",
      icon: "credit card outline",
      accent: "#635bff",
    };
  }
  return {
    key: "stripe",
    title: "Stripe",
    short: "Stripe",
    description:
      "Zahlung über Stripe. Abo, Rechnungen und Zahlungsmittel im Stripe-Kundenportal verwalten.",
    icon: "payment",
    accent: "#635bff",
  };
}

/** @param {Record<string, unknown>} details */
function statusBadge(details) {
  if (!details) return { text: "—", color: "grey" };
  if (details.active) return { text: "Aktiv", color: "green" };
  const st = String(details.status ?? "").toLowerCase();
  if (st === "2" || st === "canceled" || st === "cancelled") {
    return { text: "Gekündigt", color: "grey" };
  }
  if (st === "1" || st === "past_due" || st === "incomplete" || st === "unpaid") {
    return { text: "Zahlung ausstehend", color: "orange" };
  }
  return { text: "Inaktiv", color: "grey" };
}

function hasSubscriptionRecord(details) {
  if (!details || typeof details !== "object") return false;
  return (
    details.billing_provider != null
    || details.stripe_subscription_id
    || details.paypal_subscription_id
    || details.plan_interval_months != null
  );
}

export default function AccountSubscriptions() {
  const authToken = useSelector(getAuthToken);
  const clientAud = useSelector(getClientAud);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** @type {Record<string, unknown> | null} */
  const [details, setDetails] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const res = await fetch("/api/v1/subscriptions/details", {
          headers: authToken ? {
            Authorization: `Bearer ${authToken}`,
            JWT_AUD: clientAud,
          } : {},
          credentials: "include",
        });
        if (res.status === 401) {
          window.location.href = "/login";
          return;
        }
        if (!res.ok) throw new Error(`Fehler beim Laden (${res.status})`);
        const json = await res.json();
        if (mounted.current) setDetails(json);
      } catch (e) {
        if (mounted.current) setError(e.message);
      } finally {
        if (mounted.current) setLoading(false);
      }
    }
    load();
  }, [authToken, clientAud]);

  const payment = useMemo(() => (details && hasSubscriptionRecord(details) ? getPaymentProfile(details) : null), [details]);
  const badge = useMemo(() => statusBadge(details), [details]);
  const hasRecord = hasSubscriptionRecord(details);

  async function openPortal() {
    if (details?.billing_provider === "paypal") {
      window.open("https://www.paypal.com/myaccount/autopay/", "_blank", "noopener,noreferrer");
      return;
    }
    // Open the tab synchronously to preserve the user gesture, then point
    // it at the portal URL once the async fetch resolves (avoids popup blockers).
    const popup = window.open("about:blank", "_blank");
    try {
      const res = await fetch("/api/v1/subscriptions/portal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? {
            Authorization: `Bearer ${authToken}`,
            JWT_AUD: clientAud,
          } : {}),
        },
        credentials: "include",
      });
      if (res.status === 401) {
        if (popup) { try { popup.close(); } catch (_) { /* ignore */ } }
        window.location.href = "/login";
        return;
      }
      const json = await res.json();
      if (json.url) {
        if (popup) {
          try { popup.opener = null; } catch (_) { /* ignore */ }
          popup.location.href = json.url;
        } else {
          window.open(json.url, "_blank", "noopener,noreferrer");
        }
      } else {
        if (popup) { try { popup.close(); } catch (_) { /* ignore */ } }
        if (json.error) setError(json.message || "Fehler beim Öffnen des Kundenportals.");
      }
    } catch (e) {
      if (popup) { try { popup.close(); } catch (_) { /* ignore */ } }
      setError("Fehler beim Öffnen des Kundenportals.");
    }
  }

  async function confirmCancel() {
    setCancelling(true);
    setCancelError(null);
    try {
      const res = await fetch("/api/v1/subscriptions/cancel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? {
            Authorization: `Bearer ${authToken}`,
            JWT_AUD: clientAud,
          } : {}),
        },
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      const json = await res.json();
      if (!res.ok || json.error) {
        setCancelError(json.message || "Kündigung fehlgeschlagen. Bitte versuche es erneut.");
        return;
      }
      setDetails((prev) => (prev ? { ...prev, cancel_at_period_end: true } : prev));
      setShowCancelConfirm(false);
    } catch (e) {
      setCancelError("Kündigung fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setCancelling(false);
    }
  }

  const manageLabel = details?.billing_provider === "paypal"
    ? "Bei PayPal verwalten"
    : "Im Kundenportal verwalten";

  const panelStyle = {
    background: "#fff",
    color: "#1a1a1a",
    borderRadius: 12,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 28px rgba(0, 0, 0, 0.06)",
    padding: "1.75rem 2rem 2rem",
    marginBottom: "2rem",
  };

  const segmentOnWhite = {
    background: "#fff",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "none",
  };

  return (
    <div className="container account-subscriptions" style={{ maxWidth: 720, margin: "0 auto" }}>
      <div style={panelStyle}>
        <Header as="h2" style={{ fontWeight: 600, marginBottom: 4, color: "#1a1a1a" }}>
          Mein Abonnement
        </Header>
        <p style={{ color: "#444", marginBottom: 20, fontSize: 15, lineHeight: 1.5 }}>
          Zahlungsart, Laufzeit und Verwaltung — abhängig davon, ob du mit PayPal, Karte oder Lastschrift gebucht hast.
        </p>

        {loading && (
          <Segment
            style={{
              ...segmentOnWhite,
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader active inline="centered" size="large">
              Lade Abo-Daten…
            </Loader>
          </Segment>
        )}

        {!loading && error && <Message error onDismiss={() => setError(null)} content={error} />}

        {!loading && !error && !hasRecord && (
        <Segment placeholder style={{ ...segmentOnWhite, borderRadius: 12, textAlign: "center", padding: "3rem 2rem" }}>
          <Header icon>
            <Icon name="credit card outline" color="grey" />
            Kein Abonnement
          </Header>
          <p style={{ color: "#666", maxWidth: 400, margin: "0 auto 1.5rem" }}>
            Du hast derzeit kein aktives Abo. Wähle auf der nächsten Seite zwischen Karte, SEPA-Lastschrift oder PayPal.
          </p>
          <Button primary size="large" className="cuddle" onClick={() => { window.location.href = "/subscribe"; }}>
            <Icon name="plus" />
            Jetzt abonnieren
          </Button>
        </Segment>
        )}

        {!loading && !error && hasRecord && payment && (
        <Segment
          raised
          style={{
            ...segmentOnWhite,
            borderRadius: 12,
            overflow: "hidden",
            padding: 0,
          }}
        >
          <div
            style={{
              padding: "1.25rem 1.5rem",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              background: "#fafafa",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: `${payment.accent}12`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name={payment.icon} size="large" style={{ color: payment.accent, margin: 0 }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Zahlungsart
                </div>
                <Header as="h3" style={{ margin: 0, fontWeight: 600, color: "#1a1a1a" }}>
                  {payment.title}
                </Header>
              </div>
            </div>
            <Label color={badge.color} size="large" style={{ margin: 0 }}>
              {badge.text}
            </Label>
          </div>

          <div style={{ padding: "1.5rem" }}>
            <Grid stackable columns={2}>
              <Grid.Column>
                <List relaxed size="large" style={{ margin: 0 }}>
                  <List.Item>
                    <List.Icon name="calendar outline" verticalAlign="middle" color="teal" />
                    <List.Content>
                      <List.Header>Abrechnungszeitraum</List.Header>
                      <List.Description>{formatPlanMonths(details.plan_interval_months)}</List.Description>
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="euro" verticalAlign="middle" color="teal" />
                    <List.Content>
                      <List.Header>Preis (laut Abo)</List.Header>
                      <List.Description>
                        {formatMoneyEUR(details.price_cents, details.currency)}
                        {" "}
                        <span style={{ color: "#888", fontSize: 13 }}>
                          / {formatPlanMonths(details.plan_interval_months)}
                        </span>
                      </List.Description>
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>
              <Grid.Column>
                <List relaxed size="large" style={{ margin: 0 }}>
                  <List.Item>
                    <List.Icon name="clock outline" verticalAlign="middle" color="teal" />
                    <List.Content>
                      <List.Header>Aktuelle Periode bis</List.Header>
                      <List.Description>{formatDateDE(details.current_period_end)}</List.Description>
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="payment" verticalAlign="middle" color="teal" />
                    <List.Content>
                      <List.Header>Anbieter</List.Header>
                      <List.Description>
                        {details.billing_provider === "paypal" ? "PayPal" : "Stripe"}
                        {details.billing_provider !== "paypal" && payment.key !== "stripe" && (
                          <span style={{ color: "#888" }}>
                            {" "}
                            ·
                            {" "}
                            {payment.short}
                          </span>
                        )}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>
            </Grid>

            <Divider section />

            {details.cancel_at_period_end && details.active && (
              <Message warning icon style={{ borderRadius: 8 }}>
                <Icon name="calendar times outline" />
                <Message.Content>
                  <Message.Header>Bereits gekündigt</Message.Header>
                  <p style={{ margin: "0.5rem 0 0", lineHeight: 1.55 }}>
                    Dein Abo verlängert sich nicht mehr. Du behältst Zugang bis zum
                    {" "}
                    {formatDateDE(details.current_period_end)}
                    .
                  </p>
                </Message.Content>
              </Message>
            )}

            <Message info icon style={{ borderRadius: 8 }}>
              <Icon name="info circle" />
              <Message.Content>
                <Message.Header>So verwaltest du dieses Abo</Message.Header>
                <p style={{ margin: "0.5rem 0 0", lineHeight: 1.55 }}>{payment.description}</p>
              </Message.Content>
            </Message>

            {cancelError && <Message error onDismiss={() => setCancelError(null)} content={cancelError} />}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
              <Button
                primary
                className="cuddle"
                icon
                labelPosition="left"
                onClick={openPortal}
              >
                <Icon name="external alternate" />
                {manageLabel}
              </Button>
              {details.active && !details.cancel_at_period_end && (
                <Button basic negative onClick={() => setShowCancelConfirm(true)}>
                  <Icon name="times circle outline" />
                  Abo kündigen
                </Button>
              )}
              {!details.active && (
                <Button basic onClick={() => { window.location.href = "/subscribe"; }}>
                  <Icon name="redo" />
                  Neues Abo abschließen
                </Button>
              )}
            </div>
          </div>
        </Segment>
        )}

        <Confirm
          open={showCancelConfirm}
          header="Abo wirklich kündigen?"
          content={`Dein Abo verlängert sich dann nicht mehr, du behältst aber Zugang bis zum ${formatDateDE(details && details.current_period_end)}.`}
          confirmButton={cancelling ? "Wird gekündigt…" : "Ja, kündigen"}
          cancelButton="Zurück"
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={confirmCancel}
        />

        <WiderrufFootnoteLink style={{ marginTop: 28, marginBottom: 0 }} />
      </div>
    </div>
  );
}
